'use client'

import { useState, useEffect, ChangeEvent } from 'react'

import { useFormContext, useController, useWatch } from 'react-hook-form'

import { useDataProviderContext } from '@/shared/contexts/DataProvider'
import { cn } from '@/shared/lib/utils'
import { useFormField } from '@/shared/ui/form'

import { fileToBase64 } from './fileToBase64'
import { UserPlaceholder } from './UserPlaceholder'

interface ImageInputProps {
  accept?: string
  removable?: boolean
  base64AsValue?: boolean
  blankImage?: string
  imageFit?: 'cover' | 'contain'
  uploadArgs?: Record<string, unknown>
  valueKey?: string
  mediaApiScope?: string
  setRoleObject?: boolean
  className?: string
  circle?: boolean
  onChange?: (value: any, record?: any) => void
}

export const ImageInput = ({
  accept = '.png, .jpg, .jpeg, .gif',
  base64AsValue,
  blankImage = '/placeholders/user.svg',
  imageFit = 'cover',
  removable,
  uploadArgs,
  valueKey = 'id',
  mediaApiScope = '',
  setRoleObject = false,
  className,
  circle,
  onChange
}: ImageInputProps) => {
  const { name } = useFormField()
  const { getValues, setValue } = useFormContext()
  const { dataProvider } = useDataProviderContext()

  const {
    field: { ref, value, onChange: onChangeField, ...inputProps }
  } = useController({ name })

  const recordImageUrl = useWatch({
    name: `${name.replace('_id', '')}.file.url`
  })

  const [imageUrl, setImageUrl] = useState<string | null>(null)
  const [imageFileValue, setImageFileValue] = useState<string>('')

  useEffect(() => {
    const record = getValues()
    const url = recordImageUrl || record?.[name.replace('_id', '')]?.file?.url
    if (url) setImageUrl(url)
  }, [recordImageUrl, name])

  async function handleFileChange(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) {
      setImageUrl(null)
      onChangeField('')
      onChange?.('')
      return
    }

    const base64 = await fileToBase64(file)
    const fileData = base64AsValue
      ? base64
      : {
          data: base64,
          filename: file.name,
          size: file.size,
          content_type: file.type
        }

    try {
      const res = await dataProvider.create(
        `${mediaApiScope ? '/' + mediaApiScope : ''}/medias`,
        { file: fileData, ...(uploadArgs || {}) }
      )

      const record = res?.data?.record
      const idValue = record?.[valueKey] ?? record?.id
      const blobUrl = URL.createObjectURL(file)

      setImageUrl(blobUrl)
      setImageFileValue('')
      onChangeField(idValue)
      onChange?.(idValue, record)
      if (setRoleObject) setValue(name.replace('_id', ''), record)
    } catch (error) {
      console.error('Erro ao fazer upload da imagem', error)
      onChangeField(null)
      onChange?.(null)
    }
  }

  function handleRemove() {
    setImageUrl(null)
    setImageFileValue('')
    onChangeField('')
    onChange?.('')
  }

  return (
    <div
      className={cn(
        'relative flex aspect-square w-24 items-center justify-center overflow-hidden rounded-md border border-muted bg-muted',
        circle && 'rounded-full',
        className
      )}
    >
      {imageUrl ? (
        <img
          src={imageUrl}
          alt="Pré-visualização"
          className={cn(
            'h-full w-full object-cover',
            imageFit === 'contain' && 'object-contain'
          )}
        />
      ) : (
        <UserPlaceholder className="w-full h-full text-muted-foreground" />
      )}

      <input
        type="file"
        accept={accept}
        className="absolute inset-0 opacity-0 cursor-pointer"
        onChange={handleFileChange}
        value={imageFileValue}
        ref={ref}
      />

      {removable && imageUrl && (
        <button
          type="button"
          onClick={handleRemove}
          className="absolute right-1 top-1 z-10 rounded-full bg-white p-0.5 shadow"
        >
          <span className="text-xs font-bold text-muted-foreground">×</span>
        </button>
      )}
    </div>
  )
}
